<template>
  <div id="day_form">
    <p class="title">
      {{ title }}
    </p>

    <div id="form_body_store" v-if="showStoreForm">
      <c-store-form v-model:store="store" v-model:region="region"/>
    </div>

    <div id="form_body_cebeo_user" v-if="showInstallerForm">
      <c-cebeo-account v-model:user="installer"/>
    </div>

    <div id="form_body_time" v-if="showDateTimeForm">
      <c-day-picker v-model:date="selectedDate"/>

      <c-timeslot-picker v-model:daypart="selectedDaypart" :date="requestData.date" :store="store"
                         @clear-time="this.selectedDaypart = null"/>

      <c-input v-model:value="numberOfPersons" :max="20"
               :placeholder="$t('appointment.numberOfPersons')"/>

      <c-options-subscript v-model:value="purpose" :options="purposes"
                           :placeholder="$t('purposes.select')"/>
    </div>

    <div id="sidenote_banner" v-if="showDateTimeForm">
      <p>{{ $t('appointment.sideNoteBannerPart1') }}</p>
      <p>{{ $t('appointment.sideNoteBannerPart2') }}</p>
    </div>

    <div id="form_body_customer" v-if="showCustomerForm">
      <c-customer-form :as-professional="true" v-model:customer="customer"/>
    </div>

    <div id="form_body_address" v-if="showAddressForm">
      <c-address-form :required="false" v-model:address="address"/>
    </div>

    <div id="form_body_floorplan" v-if="showFloorplanForm">
      <p class="subtitle">{{ $t('floorplan.pdfOnly') }}</p>
      <div id="uploader">
        <input id="file_selection_input" class="hidden" type="file" accept=".pdf"
               @change="processFiles($event)"/>
        <div id="file_selector" @click="openSelector">
          <img class="file_icon" src="../assets/icons/file.svg"/>
          <p id="add_plan">{{ $t('floorplan.addPlan') }}</p>
        </div>
        <div class="image_preview" v-for="(url, index) in previewurls">
          <p class="mx-4 overflow-hidden whitespace-nowrap overflow-ellipsis">{{ url }}</p>
          <img class="file_icon" src="../assets/icons/file.svg"/>
          <div @click="deletePlan(index)"><img class="trash" src="../assets/icons/trash_red.svg"/>
          </div>
          <div @click="preview(url)"><img class="zoom" src="../assets/icons/zoom.svg"/></div>
        </div>
      </div>
    </div>

    <div id="form_body_confirm_data" v-if="showConfirmForm">
      <div class="confirm_group">
        <p>{{ $t('confirm.date') }}: {{ this.toddmmyyyy(selectedDate) }}</p>
        <p>{{ $t('confirm.daypart') }}: {{ $t(`dayparts.${selectedDaypart}`) }}</p>
      </div>
      <div class="confirm_group">
        <p>{{ customer.name }} {{ customer.firstName }}</p>
        <p>{{ customer.email }}</p>
        <p>{{ customer.gsm }}</p>
      </div>
      <div class="confirm_group">
        <p>{{ address.street }} {{ address.number }}<label v-if="address.busNumber">,
          {{ $t('address.busNumber') }} {{ address.busNumber }}</label></p>
        <p>{{ address.postCode }} {{ address.city }}</p>
        <p>{{ address.country }}</p>
      </div>
      <div class="confirm_group">
        <div class="policy mt-8 flex items-center">
          <input id="policyBox" type="checkbox" v-model="readPolicy" class="mr-2"/>
          <label for="policyBox">
            {{ $t('policy.iHave') }}
            <a :href="policy" target="_blank">{{ $t('policy.privacyPolicy') }}</a>
            {{ $t('policy.readAndAccepted') }}
          </label>
        </div>
        <div class="policy flex items-center">
          <input id="notifyCustomerBox" type="checkbox" v-model="notifyCustomer" class="mr-2"/>
          <label for="notifyCustomerBox">
            {{ $t('confirm.notifyCustomer') }}
          </label>
        </div>
        <div class="policy flex items-center">
          <input id="installerPresent" type="checkbox" v-model="installerPresent" class="mr-2"/>
          <label for="installerPresent">
            {{ $t('confirm.professionalPresent') }}
          </label>
        </div>
      </div>
    </div>

    <div id="form_body_confirm_mail" v-if="showMailForm">
      <p class="font-bold">{{ $t('ending.confirmMail') }}</p>
    </div>
    <c-navigation v-if="!showMailForm" id="navigation" :forwardDisabled="forwardDisabled"
                  :lastStep="showConfirmForm"
                  @back="back"
                  @continue="next"/>
  </div>
</template>

<script>
import DayPicker from '@/components/form/DayPicker';
import NavigationButtons from '@/components/form/NavigationButtons';
import TimeslotPicker from '@/components/form/TimeslotPicker';
import Input from '@/components/form/Input';
import CustomerForm from '@/components/CustomerForm';
import customerDataVerification from '@/mixins/customerDataVerification';
import AddressForm from '@/components/AddressForm';
import dateConversion from '@/mixins/dateConversion';
import formatTime from '@/mixins/formatTime';
import OptionsDropdown from '@/components/form/OptionsDropdown';
import CebeoAccountForm from '@/components/CebeoAccountForm';
import purposes from '../mixins/purposes';
import OptionsDropdownWithSubscript from './form/OptionsDropdownWithSubscript';
import StoreForm from './StoreForm';

export default {
  name: 'AppointmentAsProfessional',

  mixins: [customerDataVerification, dateConversion, formatTime, purposes],

  components: {
    'c-day-picker': DayPicker,
    'c-timeslot-picker': TimeslotPicker,
    'c-navigation': NavigationButtons,
    'c-input': Input,
    'c-customer-form': CustomerForm,
    'c-address-form': AddressForm,
    'c-options': OptionsDropdown,
    'c-cebeo-account': CebeoAccountForm,
    'c-options-subscript': OptionsDropdownWithSubscript,
    'c-store-form': StoreForm,
  },

  computed: {
    policy() {
      return process.env.VUE_APP_PRIVACYPOLICY;
    },

    showStoreForm() {
      return this.step === 0;
    },

    showInstallerForm() {
      return this.step === 1;
    },

    showDateTimeForm() {
      return this.step === 2;
    },

    showCustomerForm() {
      return this.step === 3;
    },

    showAddressForm() {
      return this.step === 4;
    },

    showFloorplanForm() {
      return this.step === 5;
    },

    showConfirmForm() {
      return this.step === 6;
    },

    showMailForm() {
      return this.step === 7;
    },

    previewurls() {
      let urls = [];
      if (!this.files) {
        return urls;
      }

      this.files.forEach((file) => {
        urls.push(URL.createObjectURL(file));
      });
      return urls;
    },

    stores() {
      return this.$store.getters.getStores.map((store) => {
        return {
          id: store.id,
          value: store[`name_${this.$i18n.locale}`]
        };
      });
    },

    forwardDisabled() {
      if (this.showStoreForm) {
        return this.store === null;
      }

      if (this.showInstallerForm) {
        return !this.installer.company || !this.isValidEmail(this.installer.email) || !this.installer.email || !this.installer.customerNumber || !this.installer.gsm;
      }

      if (this.showDateTimeForm) {
        return !this.selectedDate || !this.selectedDaypart || this.numberOfPersons <= 0 || this.numberOfPersons > 20 || !this.purpose;
      }

      if (this.showCustomerForm) {
        return this.hasCustomerErrors;
      }

      if (this.showAddressForm) {
        return false;
      }

      if (this.showFloorplanForm) {
        return this.files.length === 0;
      }

      if (this.showConfirmForm) {
        return !this.readPolicy;
      }

      return true;
    },

    title() {
      let titleMapping = {
        0: this.$t('store.select'),
        1: this.$t('cebeoUser.title'),
        2: this.$t('appointment.pickDayTime'),
        3: this.$t('customer.registerFormProfessional'),
        4: this.$t('customer.addressTitle'),
        5: this.$t('floorplan.title'),
        6: this.$t('confirm.title'),
        7: this.$t('ending.title')
      };

      return titleMapping[this.step];
    },

    requestData() {
      return {
        date: this.selectedDate ? this.toValidPhpDate(this.selectedDate) : '',
        daypart: this.selectedDaypart,
        firstName: this.customer.firstName,
        name: this.customer.name,
        email: this.customer.email,
        phone: this.customer.gsm,
        language: this.$root.$i18n.locale,
        ambienceId: this.customer.ambience ? this.customer.ambience.id : null,
        address: this.address,
        numberOfVisitors: this.numberOfPersons,
        storeId: this.store ? this.store.id : null,
        installerName: this.installer.company,
        installerEmail: this.installer.email,
        installerFirstName: this.installer.firstName,
        installerFamilyName: this.installer.familyName,
        installerPhone: this.installer.gsm,
        customerNumber: this.installer.customerNumber,
        installerPresent: this.installerPresent,
        notifyCustomer: this.notifyCustomer,
        createdByProfessional: true,
        purpose: this.purpose ? this.purpose.id : null,
      };
    }
  },

  data() {
    return {
      step: 0,
      store: null,
      region: null,
      selectedDate: null,
      selectedDaypart: null,
      numberOfPersons: null,
      purpose: null,
      customer: {
        name: '',
        firstName: '',
        email: '',
        gsm: '',
        ambience: null
      },
      address: {
        street: '',
        number: '',
        busNumber: '',
        postCode: '',
        city: '',
        country: ''
      },
      installer: {
        email: '',
        company: '',
        customerNumber: '',
        firstName: '',
        familyName: '',
        phone: ''
      },
      validation: false,
      files: [],
      file: null,
      readPolicy: false,
      installerPresent: false,
      notifyCustomer: false,
    };
  },

  created() {
    if (this.stores.length === 1) {
      this.store = this.stores[0];
      this.step++;
    }
  },

  methods: {
    back() {
      const isOnlyOneStoreAvailable = this.step === 1 && this.stores.length === 1;
      const isOnFirstStep = this.step === 0;
      if (isOnlyOneStoreAvailable || isOnFirstStep) {
        this.$emit('back');
        return;
      }

      if (this.showConfirmForm && this.purpose && this.purpose.id === 'ADVICE') {
        this.step--;
      }

      this.step--;
    },

    next() {
      if (this.showConfirmForm) {
        this.createVisit();
      }

      if (this.showAddressForm && this.purpose && this.purpose.id === 'ADVICE') {
        this.step++;
      }

      this.step++;
    },

    processFiles(event) {
      this.files.push(event.target.files[0]);
    },

    preview(url) {
      window.open(url);
    },

    openSelector() {
      document.getElementById('file_selection_input')
        .click();
    },

    deletePlan(index) {
      this.files.splice(index, 1);
    },

    createVisit() {
      this.$store.dispatch('createVisit', this.requestData)
        .then((res) => {
          let visitId = res.data.id;
          this.files.forEach((file) => {
            this.$store.dispatch('uploadFloorplan', {
              customerVisitId: visitId,
              file: file,
            });
          });
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }
};
</script>

<style scoped>
input[type='checkbox'] {
  width: 15px !important;
  height: 15px !important;
}

a {
  @apply text-cebeo_blue underline;
}

#form_body_store {
  width: 600px;
  @apply mx-auto;
}

#day_form {
  width: 100%;
  height: 550px;
  @apply relative;
}

#form_body_time {
  height: 240px;
  max-width: 600px;
  @apply grid grid-rows-3 mx-auto;
}

#sidenote_banner {
  max-width: 600px;
  margin-top: 15px;
  @apply text-left italic font-semibold text-sm mx-auto;
}

#file_selector {
  width: 144px;
  height: 162px;
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23CECECEFF' stroke-width='3' stroke-dasharray='12%2c 16' stroke-dashoffset='24' stroke-linecap='square'/%3e%3c/svg%3e");
  @apply flex justify-end flex flex-col items-center;
}

#add_plan {
  @apply mt-8 mb-4 uppercase text-xs text-cebeo_gray;
}

#uploader {
  @apply flex gap-x-2 justify-center;
}

#form_body_floorplan {
  @apply relative;
}

#form_body_cebeo_user {
  @apply z-50;
}

.subtitle {
  top: -50px;
  @apply absolute text-center font-bold w-full;
}

.file_icon {
  width: 50px;
  height: 62px;
  @apply mx-auto;
}

.image_preview > .file_icon {
  @apply mt-3;
}

.image_preview {
  width: 144px;
  height: 162px;
  @apply bg-cebeo_blue_faded flex flex-col relative;
}

.trash {
  bottom: 0;
  left: 0;
  size: 35px;
  @apply absolute p-1
}

.zoom {
  bottom: 0;
  right: 0;
  size: 35px;
  @apply absolute bg-cebeo_blue p-1;
}

.confirm_group {
  width: 300px;
  @apply text-left mx-auto font-bold mt-4;
}

.policy > input[type='checkbox'] {
  width: 25px;
}

.policy > label {
  width: calc(100% - 25px);
}

.policy {
  font-size: 0.6rem;
  @apply mt-2
}

@media only screen and (max-width: 800px) {
  #day_form {
    width: 90%;
    height: 600px;
    @apply relative mx-auto;
  }

  #form_body_installer {
    width: 100%
  }

  #uploader {
    max-height: 350px;
    @apply w-full flex gap-2 flex-wrap justify-center overflow-y-auto;
  }

  #uploader::-webkit-scrollbar {
    width: 0px;
  }
}
</style>
